import { useState } from "react";
import PropTypes from "prop-types";
import "./BookingsTable.css";
import { formatCurrency, formatDate } from "../../utils/utils";
import {
  PLATFORM_FEE_BILLING_ITEM,
  EXISTING_DISPUTE_MESSAGE,
  OVERDUE_DISPUTE_MESSAGE,
} from "../../constants/disputes";

const isPlatformFee = (billingItem) =>
  (billingItem || "").toLowerCase().replace(/[\s_]/g, "") ===
  PLATFORM_FEE_BILLING_ITEM;

const isOverdue = (booking) =>
  (booking.payment_status || "").toUpperCase() === "OVERDUE";

const BookingStatusPill = ({ status }) => {
  const getStatusClass = () => {
    switch (status.toLowerCase()) {
      case "confirmed":
        return "booking-status--confirmed";
      case "cancelled":
        return "booking-status--cancelled";
      default:
        return "";
    }
  };

  return (
    <span className={`booking-status-pill ${getStatusClass()}`}>{status}</span>
  );
};

BookingStatusPill.propTypes = {
  status: PropTypes.string.isRequired,
};

const PaymentStatusPill = ({ status }) => {
  const getStatusClass = () => {
    switch (status?.toLowerCase()) {
      case "paid":
        return "payment-status--paid";
      case "pending":
        return "payment-status--pending";
      case "overdue":
        return "payment-status--overdue";
      case "refund":
      case "refunded":
        return "payment-status--refund";
      default:
        return "";
    }
  };

  if (!status) return null;

  const displayStatus = status.toUpperCase() === "REFUND" ? "REFUNDED" : status;

  return (
    <span className={`payment-status-pill ${getStatusClass()}`}>
      {displayStatus}
    </span>
  );
};

PaymentStatusPill.propTypes = {
  status: PropTypes.string,
};

const TransactionTypePill = ({ type }) => {
  if (!type) return null;

  const getTypeClass = () => {
    switch (type?.toLowerCase()) {
      case "payment":
        return "transaction-type--payment";
      case "refund":
        return "transaction-type--refund";
      case "credit":
        return "transaction-type--credit";
      default:
        return "";
    }
  };

  return (
    <span className={`transaction-type-pill ${getTypeClass()}`}>{type}</span>
  );
};

TransactionTypePill.propTypes = {
  type: PropTypes.string,
};

const BillingItemPill = ({ billingItem }) => {
  if (!billingItem)
    return (
      <span className="billing-item-pill billing-item--booking">Booking</span>
    );

  const getItemClass = () => {
    switch (billingItem.toLowerCase().replace(/[\s_]/g, "")) {
      case "platformfee":
        return "billing-item--platform-fee";
      case "booking":
      default:
        return "billing-item--booking";
    }
  };

  const displayLabel =
    billingItem.toLowerCase().replace(/[\s_]/g, "") === "platformfee"
      ? "Platform Fee"
      : "Booking";

  return (
    <span className={`billing-item-pill ${getItemClass()}`}>
      {displayLabel}
    </span>
  );
};

BillingItemPill.propTypes = {
  billingItem: PropTypes.string,
};

const BookingsTable = ({
  bookings,
  selectedAccount,
  onDispute,
  onViewDispute,
  openDisputeIds = [],
}) => {
  const openDisputeSet = new Set(openDisputeIds);
  const isDisputeEnabled = typeof onDispute === "function";
  const isViewDisputeEnabled = typeof onViewDispute === "function";

  // Row-click dispute popover: { x, y, booking, hasOpenDispute }.
  const [menu, setMenu] = useState(null);

  const isDisputable = (booking) =>
    isDisputeEnabled && !isPlatformFee(booking.billing_item);

  const handleRowClick = (e, booking) => {
    if (!isDisputable(booking)) return;
    const hasOpenDispute =
      booking.has_open_dispute === true ||
      openDisputeSet.has(booking.ht_confirmation_id);
    setMenu({
      x: e.clientX,
      y: e.clientY,
      booking,
      hasOpenDispute,
      overdue: isOverdue(booking),
    });
  };

  const closeMenu = () => setMenu(null);

  const handleDispute = () => {
    if (menu && !menu.hasOpenDispute && !menu.overdue) {
      onDispute(menu.booking);
    }
    closeMenu();
  };

  const handleViewDispute = () => {
    if (menu && menu.hasOpenDispute && isViewDisputeEnabled) {
      onViewDispute(menu.booking);
    }
    closeMenu();
  };

  return (
    <div className="bookings-table-container">
      <table className="bookings-table">
        <thead>
          <tr>
            <th>Billing Item</th>
            <th>Booking Date</th>
            <th>Booking Status</th>
            <th>Booking Type</th>
            <th>HT Confirmation ID</th>
            <th>Client Confirmation ID</th>
            <th>Property Name</th>
            <th>Guest Name</th>
            <th>Check-In Date</th>
            <th>Check-Out Date</th>
            <th>Transaction Type</th>
            <th>HT Booking Amount</th>
            <th>Transaction Amount</th>
            <th>Transaction Status</th>
            <th>Transaction Date</th>
            <th>Cancelled At (EST)</th>
            <th>CXL Cut-Off Time (EST)</th>
          </tr>
        </thead>
        <tbody>
          {bookings.map((booking, index) => (
            <tr
              key={index}
              className={isDisputable(booking) ? "bookings-row--clickable" : ""}
              onClick={(e) => handleRowClick(e, booking)}
            >
              <td>
                <BillingItemPill billingItem={booking.billing_item} />
              </td>
              <td>{formatDate(booking.booking_date)}</td>
              <td>
                <BookingStatusPill status={booking.booking_status} />
              </td>
              <td>{booking.booking_type}</td>
              <td>{booking.ht_confirmation_id}</td>
              <td>{booking.client_room_confirmation_id}</td>
              <td>{booking.property_name}</td>
              <td>{booking.guest_name}</td>
              <td>{formatDate(booking.check_in_date)}</td>
              <td>{formatDate(booking.check_out_date)}</td>
              <td>
                <TransactionTypePill type={booking.transaction_type} />
              </td>
              <td>
                {booking.ht_booking_amount === null ||
                booking.ht_booking_amount === undefined
                  ? "-"
                  : formatCurrency(
                      booking.ht_booking_amount,
                      selectedAccount?.currency || "USD"
                    )}
              </td>
              <td>
                {booking.payment_amount === null ||
                booking.payment_amount === undefined
                  ? "-"
                  : booking.payment_status?.toUpperCase() === "REFUND" &&
                      booking.payment_amount !== 0
                    ? `-${formatCurrency(
                        Math.abs(booking.payment_amount),
                        selectedAccount?.currency || "USD"
                      )}`
                    : formatCurrency(
                        booking.payment_amount,
                        selectedAccount?.currency || "USD"
                      )}
              </td>
              <td>
                <PaymentStatusPill status={booking.payment_status} />
              </td>
              <td>{formatDate(booking.payment_date)}</td>
              <td>
                {!booking.cancelled_at_est ||
                booking.cancelled_at_est === null ||
                booking.cancelled_at_est === undefined ||
                booking.cancelled_at_est === "" ||
                booking.cancelled_at_est === 0
                  ? "-"
                  : booking.cancelled_at_est}
              </td>
              <td>
                {!booking.cxl_cut_off_time_est ||
                booking.cxl_cut_off_time_est === null ||
                booking.cxl_cut_off_time_est === undefined ||
                booking.cxl_cut_off_time_est === "" ||
                booking.cxl_cut_off_time_est === 0
                  ? "-"
                  : booking.cxl_cut_off_time_est}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {menu && (
        <>
          <div className="bookings-menu-overlay" onClick={closeMenu} />
          <div
            className="bookings-context-menu"
            style={{ top: menu.y, left: menu.x }}
            role="menu"
          >
            {menu.hasOpenDispute ? (
              <div className="bookings-context-menu__disabled">
                <span className="bookings-context-menu__item-label">
                  Dispute
                </span>
                <span className="bookings-context-menu__error">
                  {EXISTING_DISPUTE_MESSAGE}
                </span>
                {isViewDisputeEnabled && (
                  <button
                    type="button"
                    className="bookings-context-menu__item bookings-context-menu__view"
                    onClick={handleViewDispute}
                  >
                    View Dispute
                  </button>
                )}
              </div>
            ) : menu.overdue ? (
              <div className="bookings-context-menu__disabled">
                <span className="bookings-context-menu__item-label">
                  Dispute
                </span>
                <span className="bookings-context-menu__error">
                  {OVERDUE_DISPUTE_MESSAGE}
                </span>
              </div>
            ) : (
              <button
                type="button"
                className="bookings-context-menu__item"
                onClick={handleDispute}
              >
                Dispute
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

BookingsTable.propTypes = {
  bookings: PropTypes.arrayOf(
    PropTypes.shape({
      billingItem: PropTypes.string,
      bookingDate: PropTypes.string.isRequired,
      bookingStatus: PropTypes.string.isRequired,
      bookingType: PropTypes.string.isRequired,
      htConfirmationId: PropTypes.string.isRequired,
      clientConfirmationId: PropTypes.string.isRequired,
      propertyName: PropTypes.string.isRequired,
      guestName: PropTypes.string.isRequired,
      checkInDate: PropTypes.string.isRequired,
      checkOutDate: PropTypes.string.isRequired,
      transactionType: PropTypes.string,
      htBookingAmount: PropTypes.number,
      paymentAmount: PropTypes.number,
      paymentStatus: PropTypes.string,
      paymentDate: PropTypes.string,
      paymentDueDate: PropTypes.string,
      cancelledAt: PropTypes.string,
      cklCutOffTime: PropTypes.string,
      cklAmount: PropTypes.number,
      creditAmount: PropTypes.number,
      creditDate: PropTypes.string,
      balanceUpdatedDate: PropTypes.string,
      balanceAfter: PropTypes.number,
    })
  ).isRequired,
  selectedAccount: PropTypes.object,
  onDispute: PropTypes.func,
  onViewDispute: PropTypes.func,
  openDisputeIds: PropTypes.arrayOf(PropTypes.string),
};

export default BookingsTable;
